<template>
  <div
    class="agent-chat flex flex-col"
    style="
      min-height: 100vh;
      background: linear-gradient(165deg, #eef6ff 0%, #f7f4ff 45%, #fff1f6 100%);
    "
  >
    <!-- 顶栏：内容变长时吸顶保留 -->
    <header
      class="sticky top-0 z-20 bg-white border-b border-neutral-200 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0"
    >
      <button
        @click="goBack"
        class="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-neutral-600 transition-colors flex-shrink-0"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <div
        class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
      >
        <img
          v-if="agent && agent.avatar && !avatarBroken"
          :src="agent.avatar"
          class="w-full h-full object-cover"
          @error="avatarBroken = true"
        />
        <span v-else>{{ agent ? firstChar(agent.name) : "A" }}</span>
      </div>
      <div class="flex-1 min-w-0">
        <h1 class="font-bold text-neutral-900 truncate">
          {{ agent ? agent.name : "智能体" }}
        </h1>
        <p class="text-xs text-neutral-500 truncate">
          {{ agent ? agent.intro || "AI 智能体" : "加载中..." }}
          <span
            v-if="guestRemaining !== null && guestRemaining >= 0"
            class="ml-1 text-orange-500"
          >
            游客今日剩余 {{ guestRemaining }} 次，登录后不限次数
          </span>
        </p>
      </div>
      <button
        v-if="canToggleMode"
        @click="toggleMode"
        class="px-3 py-1.5 rounded-lg border border-neutral-300 text-neutral-500 hover:text-primary hover:border-primary text-xs transition-colors"
      >
        {{ mode === "embed" ? "站内对话" : "网页模式" }}
      </button>
      <button
        v-if="!isEmbed && messages.length > 1"
        @click="clearChat"
        class="px-3 py-1.5 rounded-lg border border-neutral-300 text-neutral-500 hover:text-primary hover:border-primary text-xs transition-colors"
      >
        新会话
      </button>
    </header>

    <!-- 对话区：扣子官方 SDK 嵌入（开场白 + 快捷提问 + 文件上传） -->
    <div v-if="isEmbed" class="flex-1 px-4 py-4">
      <div class="max-w-4xl mx-auto">
        <div
          class="relative bg-white border border-neutral-200 rounded-2xl overflow-hidden shadow-sm"
          style="height: calc(100vh - 150px); min-height: 560px"
        >
          <div ref="cozeStage" class="w-full h-full"></div>
          <div
            v-if="cozeLoading"
            class="absolute inset-0 bg-white/90 flex flex-col items-center justify-center text-sm text-neutral-500"
          >
            <i class="fa fa-circle-o-notch fa-spin mb-2 text-lg text-primary"></i>
            正在连接扣子智能体，首次打开约需 5-10 秒…
          </div>
        </div>
        <p class="text-xs text-neutral-400 mt-2 text-center">
          由扣子提供对话能力，支持上传 PDF / Word 等文档（单个文件建议不超过
          10MB，超过 20MB 会提示 Upload failure）；AI 生成内容请自行核实
        </p>
      </div>
    </div>

    <!-- 对话区：站内代理 -->
    <div v-else class="flex-1 px-4 py-6" ref="bodyEl">
      <div class="max-w-3xl mx-auto">
        <!-- 首页：欢迎区 + 快捷分类 + 热门问题（未开始对话时展示） -->
        <div v-if="!hasChat" class="pb-4">
          <div
            class="relative rounded-2xl overflow-hidden mb-6 px-6 py-7"
            style="
              background: linear-gradient(
                120deg,
                #dbeafe 0%,
                #e0f2fe 50%,
                #ede9fe 100%
              );
            "
          >
            <p class="text-lg md:text-xl font-bold" style="color: #1e3a8a">
              您好，我是{{ agent ? agent.name : "智能助手" }}～
            </p>
            <p class="text-xs mt-1.5 pr-24" style="color: #3b5b8c">
              {{
                (agent && agent.intro) ||
                "有什么可以帮你？直接提问，或点下面的快捷入口"
              }}
            </p>
            <button
              v-if="hasHistory"
              @click="loadHistory"
              class="absolute right-4 top-4 text-xs px-2.5 py-1 rounded-full bg-white/70 hover:bg-white transition-colors"
              style="color: #1e3a8a"
            >
              <i class="fa fa-history mr-1"></i>上次会话
            </button>
          </div>

          <div
            v-if="quickEntries.length"
            class="grid grid-cols-4 gap-2 md:gap-4 mb-6"
          >
            <button
              v-for="(q, i) in quickEntries"
              :key="'q' + i"
              @click="askQuick(q)"
              class="flex flex-col items-center gap-2 py-3 rounded-2xl bg-white/60 hover:bg-white transition-shadow hover:shadow-sm"
            >
              <span
                class="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg"
                :style="{ background: entryBg[i % entryBg.length] }"
              >
                <i class="fa" :class="entryIcons[i % entryIcons.length]"></i>
              </span>
              <span
                class="text-[11px] md:text-xs text-neutral-700 leading-tight px-1 text-center"
              >
                {{ q }}
              </span>
            </button>
          </div>

          <div class="bg-white rounded-2xl border border-neutral-200 p-5">
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-bold text-neutral-900">热门问题</h3>
              <button
                v-if="suggestedQuestions.length > 3"
                class="text-xs text-primary hover:underline"
                @click="shuffleHot"
              >
                换一批 <i class="fa fa-refresh ml-0.5"></i>
              </button>
            </div>
            <div v-if="hotQuestions.length" class="space-y-1">
              <button
                v-for="(q, i) in hotQuestions"
                :key="'h' + i"
                @click="askQuick(q)"
                class="w-full flex items-start gap-2 text-left text-sm text-neutral-700 hover:text-primary py-1.5 transition-colors"
              >
                <i class="fa fa-fire text-orange-400 mt-0.5"></i>
                <span class="flex-1">{{ q }}</span>
              </button>
            </div>
            <p v-else class="text-xs text-neutral-400">
              后台还没配置快捷提问，直接在下方输入框提问即可。
            </p>
          </div>
        </div>

        <template v-if="hasChat">
        <div
          v-if="cozeError"
          class="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
        >
          {{ cozeError }}
        </div>
        <div v-for="(m, i) in messages" :key="i" class="mb-5">
          <!-- 用户 -->
          <div v-if="m.role === 'user'" class="flex justify-end">
            <div
              class="bg-primary text-white rounded-2xl rounded-br-md px-4 py-2.5 max-w-[78%] whitespace-pre-wrap text-sm leading-relaxed"
            >
              {{ m.content }}
            </div>
          </div>
          <!-- 智能体 -->
          <div v-else class="flex">
            <div
              class="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden"
            >
              <img
                v-if="agent && agent.avatar && !avatarBroken"
                :src="agent.avatar"
                class="w-full h-full object-cover"
                @error="avatarBroken = true"
              />
              <span v-else>{{ agent ? firstChar(agent.name) : "A" }}</span>
            </div>
            <div class="ml-2 flex-1 min-w-0">
              <div
                class="bg-white border border-neutral-200 rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[85%] whitespace-pre-wrap text-sm leading-relaxed"
              >
                <span v-if="m.loading" class="text-neutral-400">
                  <i class="fa fa-circle-o-notch fa-spin mr-1"></i>思考中…
                </span>
                <span v-else>{{ m.content }}</span>
              </div>
            </div>
          </div>
        </div>
        </template>
      </div>
    </div>

    <!-- 输入区（站内代理） -->
    <footer
      v-if="!isEmbed"
      class="sticky bottom-0 z-20 bg-white border-t border-neutral-200 px-4 py-3 flex-shrink-0"
    >
      <div class="max-w-3xl mx-auto flex items-end gap-2">
        <button
          @click="startVoice"
          class="w-11 h-11 rounded-full text-white flex items-center justify-center flex-shrink-0 transition-transform active:scale-95"
          style="background: linear-gradient(135deg, #3b82f6, #6366f1)"
          :title="listening ? '正在聆听…' : '语音输入'"
        >
          <i :class="listening ? 'fa fa-circle-o-notch fa-spin' : 'fa fa-microphone'"></i>
        </button>
        <div class="flex-1 relative">
          <textarea
            v-model="input"
            rows="1"
            placeholder="请输入您要咨询的问题"
            class="w-full pl-4 pr-11 py-3 rounded-2xl border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm max-h-32"
            @keydown.enter.exact.prevent="send"
          ></textarea>
          <button
            class="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-500 flex items-center justify-center transition-colors"
            title="更多"
            @click="moreMenu = !moreMenu"
          >
            <i class="fa fa-plus"></i>
          </button>
          <div
            v-if="moreMenu"
            class="absolute right-0 bottom-12 w-40 bg-white rounded-xl shadow-lg border border-neutral-100 py-1 z-30"
          >
            <button
              class="block w-full text-left px-3 py-2 text-xs text-neutral-600 hover:bg-neutral-50"
              @click="moreMenu = false; clearChat()"
            >
              开启新会话
            </button>
            <button
              v-if="hasHistory"
              class="block w-full text-left px-3 py-2 text-xs text-neutral-600 hover:bg-neutral-50"
              @click="moreMenu = false; loadHistory()"
            >
              加载上次会话
            </button>
          </div>
        </div>
        <button
          @click="send"
          :disabled="loading || !input.trim()"
          class="px-5 py-3 rounded-2xl bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <i v-if="loading" class="fa fa-spinner fa-spin"></i>
          <span v-else>发送</span>
        </button>
      </div>
    </footer>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";

/** 扣子官方聊天框 SDK：内嵌在页面容器里（不是右下角悬浮球） */
const COZE_SDK_SRC =
  "https://lf-cdn.coze.cn/obj/unpkg/flow-platform/chat-app-sdk/1.1.0-beta.0/libs/cn/index.js";

export default {
  name: "AgentChat",
  data() {
    return {
      agentId: "",
      agent: null,
      agents: [],
      messages: [],
      input: "",
      loading: false,
      conversationId: "",
      /** 游客今日剩余次数；已登录为 null */
      guestRemaining: null,
      /** 当前渲染模式：embed-扣子网页 SDK / api-站内代理 */
      mode: "api",
      cozeLoading: false,
      cozeError: "",
      /** 头像加载失败（后台可能填了非图片地址）时回退成名称首字 */
      avatarBroken: false,
      /** 热门问题轮换起始下标 */
      hotIndex: 0,
      /** 「+」更多菜单 */
      moreMenu: false,
      /** 语音输入中 */
      listening: false,
      /** 本地是否有历史会话 */
      hasHistory: false,
    };
  },
  computed: {
    /** 是否使用扣子官方 SDK 内嵌（需要 botId，且仅扣子平台支持） */
    isEmbed() {
      const a = this.agent;
      return !!(
        a &&
        this.mode === "embed" &&
        a.platform !== "openai" &&
        a.botId
      );
    },
    /** 有 botId 的扣子智能体才允许在两种模式间切换 */
    canToggleMode() {
      const a = this.agent;
      return !!(a && a.platform !== "openai" && a.botId);
    },
    /** 快捷提问：后台按行填写，最多取 4 条 */
    suggestedQuestions() {
      const raw = (this.agent && this.agent.suggestedQuestions) || "";
      return raw
        .split(/\r?\n|[;；]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 12);
    },
    /** 是否已开始对话（有用户消息后就不再显示首页） */
    hasChat() {
      return (this.messages || []).some((m) => m.role === "user");
    },
    /** 顶部快捷分类：取后台配置的前 4 条快捷提问 */
    quickEntries() {
      return this.suggestedQuestions.slice(0, 4);
    },
    /** 热门问题：每次展示 3 条，可「换一批」轮换 */
    hotQuestions() {
      const all = this.suggestedQuestions;
      if (!all.length) return [];
      if (all.length <= 3) return all;
      const out = [];
      for (let i = 0; i < 3; i++) {
        out.push(all[(this.hotIndex + i) % all.length]);
      }
      return out;
    },
    entryBg() {
      return [
        "linear-gradient(135deg,#93c5fd,#60a5fa)",
        "linear-gradient(135deg,#fca5a5,#f87171)",
        "linear-gradient(135deg,#86efac,#4ade80)",
        "linear-gradient(135deg,#a5b4fc,#818cf8)",
      ];
    },
    entryIcons() {
      return [
        "fa-file-text-o",
        "fa-exclamation-triangle",
        "fa-cogs",
        "fa-line-chart",
      ];
    },
  },
  watch: {
    "$route.params.id"(id) {
      if (id && id !== this.agentId) {
        this.agentId = id;
        this.resetHistoryFlag();
        this.getAgents();
      }
    },
  },
  created() {
    // 非响应式：SDK 实例、定时器与加载 Promise 不放进 data，避免被 Vue 深度观察
    this.cozeClient = null;
    this.cozeSdkPromise = null;
    this.__syncTimer = null;
    this.__inlineKey = "";
    this.agentId = this.$route.params.id;
    this.resetHistoryFlag();
    this.getAgents();
  },
  beforeUnmount() {
    this.teardownCoze();
  },
  methods: {
    /** 点快捷问题：填入输入框并直接发送 */
    askQuick(q) {
      if (!q) return;
      this.input = q;
      this.send();
    },
    /** 热门问题换一批 */
    shuffleHot() {
      const len = Math.max(1, this.suggestedQuestions.length);
      this.hotIndex = (this.hotIndex + 3) % len;
    },
    /** 历史会话的本地存储键（按智能体区分） */
    histKey() {
      return "agent_chat_hist_" + (this.agentId || "");
    },
    saveHistory() {
      try {
        const msgs = (this.messages || []).filter(
          (m) => m && m.content && !m.loading
        );
        if (!msgs.length) return;
        localStorage.setItem(this.histKey(), JSON.stringify(msgs.slice(-30)));
        this.hasHistory = true;
      } catch (e) {
        /* 忽略隐私模式下的写入失败 */
      }
    },
    loadHistory() {
      try {
        const raw = localStorage.getItem(this.histKey());
        if (!raw) return;
        const msgs = JSON.parse(raw);
        if (Array.isArray(msgs) && msgs.length) {
          this.messages = msgs;
          this.$nextTick(() => this.scrollBottom());
        }
      } catch (e) {
        /* ignore */
      }
    },
    resetHistoryFlag() {
      try {
        this.hasHistory = !!localStorage.getItem(this.histKey());
      } catch (e) {
        this.hasHistory = false;
      }
    },
    /** 语音输入：走浏览器 Web Speech API，不支持的浏览器给出提示 */
    startVoice() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        ElMessage.warning("当前浏览器不支持语音输入，请用 Chrome / Edge 试试");
        return;
      }
      if (this.listening) return;
      try {
        const rec = new SR();
        rec.lang = "zh-CN";
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        this.listening = true;
        rec.onresult = (e) => {
          const r = e.results && e.results[0] && e.results[0][0];
          const text = r ? r.transcript : "";
          if (text) {
            this.input = (this.input ? this.input + " " : "") + text;
          }
        };
        rec.onerror = () => {
          this.listening = false;
        };
        rec.onend = () => {
          this.listening = false;
        };
        rec.start();
      } catch (e) {
        this.listening = false;
      }
    },
    getAgents() {
      this.$axios({ url: "agent/list" })
        .then((res) => {
          this.agents = res.data || [];
          this.agent = this.agents.find((a) => a.id == this.agentId) || null;
          this.applyAgent();
        })
        .catch(() => {});
    },
    /** 按后台配置决定用扣子内嵌还是站内对话 */
    applyAgent() {
      const a = this.agent;
      if (!a) return;
      this.avatarBroken = false;
      const wantEmbed =
        a.chatMode !== "api" && a.platform !== "openai" && !!a.botId;
      this.mode = wantEmbed ? "embed" : "api";
      if (wantEmbed) {
        this.$nextTick(() => this.setupCoze());
      } else {
        this.resetMessages();
      }
    },
    resetMessages() {
      this.messages =
        this.agent && this.agent.welcome
          ? [{ role: "assistant", content: this.agent.welcome }]
          : [];
    },
    toggleMode() {
      if (this.mode === "embed") {
        this.teardownCoze();
        this.mode = "api";
        this.resetMessages();
      } else {
        this.mode = "embed";
        this.cozeError = "";
        this.$nextTick(() => this.setupCoze());
      }
    },
    /** 动态加载扣子 SDK（只加载一次，失败后可重试） */
    loadCozeSdk() {
      if (window.CozeWebSDK && window.CozeWebSDK.WebChatClient) {
        return Promise.resolve();
      }
      if (this.cozeSdkPromise) return this.cozeSdkPromise;
      this.cozeSdkPromise = new Promise((resolve, reject) => {
        const done = () => {
          if (window.CozeWebSDK && window.CozeWebSDK.WebChatClient) {
            resolve();
          } else {
            reject(new Error("SDK 未初始化"));
          }
        };
        const exist = document.getElementById("coze-web-sdk");
        if (exist) {
          exist.addEventListener("load", done);
          exist.addEventListener("error", () =>
            reject(new Error("SDK 脚本加载失败"))
          );
          return;
        }
        const s = document.createElement("script");
        s.id = "coze-web-sdk";
        s.src = COZE_SDK_SRC;
        s.async = true;
        s.onload = done;
        s.onerror = () => reject(new Error("SDK 脚本加载失败"));
        document.head.appendChild(s);
      }).catch((e) => {
        this.cozeSdkPromise = null;
        throw e;
      });
      return this.cozeSdkPromise;
    },
    /**
     * 扣子官方 SDK 只提供两种形态：右下角悬浮球 + 浮层聊天窗。
     * 这里隐藏悬浮球、自动展开聊天窗，再把 SDK 生成的浮层定位到页面容器内，
     * 视觉上就是"页面内嵌聊天框"（开场白、快捷提问、上传文件都由扣子渠道配置决定）。
     */
    async setupCoze() {
      this.teardownCoze();
      if (!this.isEmbed) return;
      this.cozeLoading = true;
      this.cozeError = "";
      try {
        await this.loadCozeSdk();
        await this.$nextTick();
        const stage = this.$refs.cozeStage;
        if (!stage) throw new Error("聊天容器未就绪");
        const width = Math.max(320, Math.floor(stage.clientWidth || 700));
        const height = Math.max(480, Math.floor(stage.clientHeight || 700));
        const uploadable = Number(this.agent.uploadable) === 1;
        this.cozeClient = new window.CozeWebSDK.WebChatClient({
          config: {
            botId: this.agent.botId,
          },
          // 旧版参数（1.0.x）仍从 componentProps / 顶层读取，保留兼容
          componentProps: { title: this.agent.name || "智能体", width, uploadable },
          ui: {
            base: { lang: "zh-CN", layout: "pc", zIndex: 1200 },
            asstBtn: { isNeed: false },
            header: { isShow: true, isNeedClose: false },
            chatBot: {
              title: this.agent.name || "智能体",
              width,
              height,
              uploadable,
            },
          },
        });
        // 自动展开聊天窗（隐藏悬浮球后必须手动打开），SDK 未就绪时由轮询兜底重试
        try {
          this.cozeClient.showChatBot();
        } catch (ignore) {
          /* 初始化稍后完成，轮询阶段会重试 */
        }
        this.startInlineSync();
      } catch (e) {
        const msg = (e && e.message) || String(e);
        this.cozeError =
          "网页嵌入加载失败（" +
          msg +
          "）。请确认该智能体已在扣子平台发布到「Chat SDK」渠道，当前已切换为站内对话。";
        this.mode = "api";
        this.resetMessages();
        this.cozeLoading = false;
      }
    },
    /** 找到 SDK 渲染的聊天浮层（包含 chatapp iframe 的那层 div） */
    findCozePanel() {
      const frames = Array.from(document.querySelectorAll("iframe"));
      const f = frames.find(
        (x) => (x.src || "").indexOf("/open-platform/sdk/chatapp") >= 0
      );
      return f ? f.parentElement : null;
    },
    /** 把浮层摆到页面容器位置，并让 iframe 铺满 */
    applyInlineLayout() {
      if (!this.isEmbed) return false;
      const stage = this.$refs.cozeStage;
      const panel = this.findCozePanel();
      if (!stage || !panel) return false;
      const rect = stage.getBoundingClientRect();
      const top = Math.round(rect.top + window.scrollY);
      const left = Math.round(rect.left + window.scrollX);
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      const key = [top, left, width, height].join(",");
      const ifr = panel.querySelector("iframe");
      // SDK 内部重渲染可能重置内联样式，所以除了尺寸变化，样式被重置时也要重新接管
      const lost =
        panel.style.position !== "absolute" ||
        panel.style.width !== width + "px" ||
        panel.style.height !== height + "px";
      if (this.__inlineKey !== key || lost) {
        this.__inlineKey = key;
        Object.assign(panel.style, {
          position: "absolute",
          top: top + "px",
          left: left + "px",
          width: width + "px",
          height: height + "px",
          margin: "0",
          borderRadius: "16px",
          overflow: "hidden",
          zIndex: "1200",
          boxShadow: "none",
        });
      }
      if (ifr) {
        if (ifr.style.width !== "100%") ifr.style.width = "100%";
        if (ifr.style.height !== "100%") ifr.style.height = "100%";
        // SDK 初始化时序不稳时会把 iframe 置为 display:none（聊天窗变空白），这里强制显示
        if (ifr.style.display !== "block") ifr.style.display = "block";
        if (getComputedStyle(ifr).display === "none") {
          ifr.style.setProperty("display", "block", "important");
        }
      }
      return true;
    },
    /** 轮询校正（SDK 内部重渲染会重置内联样式，需要持续接管） */
    startInlineSync() {
      this.stopInlineSync();
      let tries = 0;
      const tick = () => {
        if (!this.isEmbed) return;
        const ok = this.applyInlineLayout();
        if (ok) {
          this.cozeLoading = false;
        } else if (tries % 6 === 0 && this.cozeClient) {
          // 兜底：SDK 就绪晚于首次调用时，定期重试展开
          try {
            this.cozeClient.showChatBot();
          } catch (ignore) {
            /* 忽略 */
          }
        }
        tries += 1;
        // 30 秒仍未出现聊天浮层：提示渠道/网络问题并回退站内对话
        if (!ok && tries > 60) {
          this.stopInlineSync();
          this.cozeError =
            "扣子聊天框加载超时。请确认该智能体已发布到「Chat SDK」渠道，且网络可访问 api.coze.cn，当前已切换为站内对话。";
          this.mode = "api";
          this.cozeLoading = false;
          this.resetMessages();
        }
      };
      tick();
      this.__syncTimer = setInterval(tick, 500);
      window.addEventListener("resize", this.applyInlineLayout);
    },
    stopInlineSync() {
      if (this.__syncTimer) {
        clearInterval(this.__syncTimer);
        this.__syncTimer = null;
      }
      window.removeEventListener("resize", this.applyInlineLayout);
    },
    teardownCoze() {
      this.stopInlineSync();
      this.__inlineKey = "";
      try {
        if (this.cozeClient && typeof this.cozeClient.destroy === "function") {
          this.cozeClient.destroy();
        }
      } catch (e) {
        /* 忽略销毁异常 */
      }
      const panel = this.findCozePanel();
      try {
        if (panel && panel.parentElement && panel.parentElement.parentElement) {
          // 移除 SDK 注入到 body 下的浮层容器，避免切换智能体后残留
          panel.parentElement.parentElement.removeChild(panel.parentElement);
        }
      } catch (e) {
        /* 节点可能已被 destroy 清理 */
      }
      const stage = this.$refs.cozeStage;
      if (stage) stage.innerHTML = "";
      this.cozeClient = null;
    },
    async send() {
      const text = this.input.trim();
      if (!text || this.loading) return;

      this.messages.push({ role: "user", content: text });
      this.messages.push({ role: "assistant", content: "", loading: true });
      this.input = "";
      this.loading = true;
      // 用户主动发言，强制跟随到底部
      this.scrollBottom(true);

      const idx = this.messages.length - 1;
      try {
        const data = await this.$aesEncrypt({
          agentId: this.agentId,
          message: text,
          conversationId: this.conversationId,
          guestId: this.guestId(),
        });
        const token = localStorage.getItem("token");
        // 用 XHR 读取流式响应：fetch 的 reader 在部分浏览器会被缓冲到响应末尾，
        // 而 XHR 的 onprogress 能实时拿到增量文本
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(
            "POST",
            process.env.VUE_APP_API_ROOT + "/agent/chat/stream"
          );
          xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
          if (token) {
            xhr.setRequestHeader("Authorization", "Bearer " + token);
          }
          let pos = 0;
          let buf = "";
          let started = false;
          const handle = (text) => {
            buf += text;
            const parts = buf.split("\n\n");
            buf = parts.pop() || "";
            for (const p of parts) {
              const ev = (p.match(/event:\s*(\S+)/) || [])[1] || "";
              const raw = (p.match(/data:\s*(.*)/) || [])[1] || "";
              if (!raw) continue;
              let val = raw;
              try {
                val = JSON.parse(raw);
              } catch (err) {
                /* 非 JSON 时按原文处理 */
              }
              if (ev === "delta") {
                if (!started) {
                  started = true;
                  this.messages[idx].loading = false;
                }
                this.messages[idx].content += val;
                this.scrollBottom();
              } else if (ev === "conv") {
                this.conversationId = val || "";
              } else if (ev === "done") {
                if (val !== "" && Number(val) >= 0) {
                  this.guestRemaining = Number(val);
                }
              } else if (ev === "error") {
                reject(new Error(val));
                return;
              }
            }
          };
          xhr.onprogress = () => {
            const text = xhr.responseText || "";
            if (text.length > pos) {
              handle(text.slice(pos));
              pos = text.length;
            }
          };
          xhr.onload = () => {
            const text = xhr.responseText || "";
            if (text.length > pos) {
              handle(text.slice(pos));
            }
            if (!started) {
              this.messages[idx].loading = false;
              this.messages[idx].content =
                "（智能体本次没有返回内容，请换个说法再试试）";
            }
            resolve();
          };
          xhr.onerror = () => reject(new Error("连接失败"));
          xhr.send(JSON.stringify({ data }));
        });
      } catch (e) {
        const msg = e.message || "请稍后再试";
        this.messages[idx].loading = false;
        // 游客次数用完：提示并引导登录
        if (msg.indexOf("游客") >= 0) {
          this.messages[idx].content = msg;
          requireLogin(this, "继续使用智能体");
          return;
        }
        this.messages[idx].content = "抱歉，这次没能拿到回复：" + msg;
      } finally {
        this.loading = false;
        this.saveHistory();
        this.scrollBottom();
      }
    },
    /** 游客标识：本地生成并缓存，用于每日次数限制 */
    guestId() {
      try {
        let id = localStorage.getItem("guest_id");
        if (!id) {
          id =
            "g" +
            Date.now().toString(36) +
            Math.random().toString(36).slice(2, 10);
          localStorage.setItem("guest_id", id);
        }
        return id;
      } catch (e) {
        return "";
      }
    },
    clearChat() {
      this.conversationId = "";
      this.messages = this.agent && this.agent.welcome
        ? [{ role: "assistant", content: this.agent.welcome }]
        : [];
    },
    /**
     * 自动滚到底部（粘性滚动）。
     *
     * <p>流式输出时每段内容都会触发一次，为避免把往上翻阅的用户强行拉回，
     * 只有当用户本来就在底部附近时才自动跟随；force=true 用于用户主动发消息时强制跟随。
     *
     * @param force 是否强制滚动（忽略当前位置）
     */
    scrollBottom(force) {
      if (!force && !this.isNearBottom()) return;
      this.$nextTick(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          // 流式内容增长很快，用瞬时滚动，避免 smooth 动画叠加导致页面被持续拽到底部；
          // 发言/结束这类一次性滚动才用 smooth，保留滑动手感
          behavior: force ? "smooth" : "auto",
        });
      });
    },

    /** 判断视口是否处于页面底部附近（留出容差，手指/鼠标微调不影响粘性） */
    isNearBottom() {
      const doc = document.documentElement;
      const tolerance = 150;
      return window.scrollY + window.innerHeight >= doc.scrollHeight - tolerance;
    },
    goBack() {
      this.$router.push("/agents");
    },
    firstChar(name) {
      return name ? name.trim().charAt(0) : "A";
    },
  },
};
</script>
